import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormSkeleton,
  useNotify
} from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentTypes } from '../../../api'
import { ProcedureStates, ProcedureTypes } from '../../../api/Argentia/enums'
import { enumKeyToValue, enumToSelectOptionArray, enumTranslateOption } from '../../../helpers'
import { fileDownloadHelper } from '../../../helpers/fileDownloadHelper'
import { useHttpError } from '../../../hooks'
import { FileModel } from '../../../pages/comunicationCenter/tabs/files/comunicationFiles.model'
import { getFormatedUtcDate } from '../../../pages/comunicationCenter/tabs/procedures/comunicationProcedures.mapper'
import { ProcedureDetailModel } from '../../../pages/comunicationCenter/tabs/procedures/comunicationProcedures.model'
import { mapProcedureDocumentToDownloadRequest } from '../../../pages/documents/documents.mapper'
import { downloadFiles } from '../../../pages/documents/documents.service'
import { getCustomerDomain } from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'
import { styles } from '../proceduresTable/proceduresTable.styles'

interface ProcedureDetailDialogProps {
  procedure?: ProcedureDetailModel
  showFormDetail: boolean
  handleClose: () => void
}

export const ProcedureDetailDialog: React.FC<ProcedureDetailDialogProps> = ({
  procedure,
  showFormDetail,
  handleClose
}) => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'enums',
    'procedureDetailDialog',
    'comunicationProcedures'
  ])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const { error } = useNotify()
  const { showError, clearError, errorProps } = useHttpError()

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const dataTableDocumentsColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'date',
      label: t('comunicationProcedures:dataTable.columns.fileDate'),
      type: DataType.TEXT
    },
    {
      name: 'type',
      label: t('comunicationProcedures:dataTable.columns.procedureType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(DocumentTypes, 'enums:DocumentTypes')
    },
    {
      name: 'state',
      label: t('comunicationProcedures:dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(ProcedureStates, 'enums:ProcedureStates')
    },
    {
      name: 'annexed',
      label: t('comunicationProcedures:dataTable.columns.state'),
      type: DataType.EXPANDABLE
    }
  ]

  const dataTableDocumentsOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    expandableRows: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('comunicationProcedures:dataTable.analytics')
    },
    rowCustomActions: [
      {
        icon: <CloudDownloadIcon fontSize="small" />,
        tooltip: t('comunicationProcedures:dataTable.tooltips.downloadOk'),
        onClick: async (row: FileModel) => {
          if (procedure?.id && row.id) {
            const response = await downloadFiles(
              mapProcedureDocumentToDownloadRequest(procedure.id, row.id)
            )
            if (response.success && response.data) {
              fileDownloadHelper(response.data.name, response.data)
            } else {
              error(
                response.error?.message ?? t('procedureDetailDialog:notifications.error.download')
              )
            }
          }
        }
        //visibleIf: 'isFileOk'
      }
    ]
  }
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <Dialog
      maxWidth={'md'}
      fullWidth={true}
      disableBackdropClick
      disableEscapeKeyDown
      open={showFormDetail}
    >
      <DialogTitle
        disableTypography
        style={{ padding: currentTheme.spacing(4), paddingTop: currentTheme.spacing(3) }}
      >
        <Typography variant="h1" color="primary" noWrap={true}>
          {t('procedureDetailDialog:title')} {procedure?.code}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: currentTheme.spacing(4), paddingTop: 0 }}>
        {procedure ? (
          <List className={classes.resume}>
            <ListItem disableGutters>
              <Typography variant="subtitle2">
                {t('procedureDetailDialog:subtitle.type')}
              </Typography>
              <Typography variant="subtitle1">
                {enumTranslateOption(
                  enumKeyToValue(ProcedureTypes, procedure?.type),
                  ProcedureTypes,
                  'enums:ProcedureTypes'
                )}
              </Typography>
            </ListItem>
            <ListItem disableGutters>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t('procedureDetailDialog:subtitle.client')}
                  </Typography>
                  <Typography variant="subtitle1">{procedure?.client}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t('procedureDetailDialog:subtitle.state')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {enumTranslateOption(
                      enumKeyToValue(ProcedureStates, procedure?.state),
                      ProcedureStates,
                      'enums:ProcedureStates'
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </ListItem>
            <ListItem disableGutters>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t('procedureDetailDialog:subtitle.cups')}
                  </Typography>
                  <Typography variant="subtitle1">{procedure?.cups}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t('procedureDetailDialog:subtitle.creationDate')}
                  </Typography>
                  <Typography variant="subtitle1">{getFormatedUtcDate(procedure?.date)}</Typography>
                </Grid>
              </Grid>
            </ListItem>
            <ListItem disableGutters>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t('procedureDetailDialog:subtitle.distributor')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {procedure?.cupsModel?.distributor?.description}
                  </Typography>
                </Grid>
                {procedure?.cupsModel?.distributor?.distributorGroup && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      {t('procedureDetailDialog:subtitle.distributorGroup')}
                    </Typography>
                    <Typography variant="subtitle1">
                      {procedure?.cupsModel?.distributor?.distributorGroup.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </ListItem>
          </List>
        ) : (
          <FormSkeleton elevation={0} columns={1} inputs={1} />
        )}

        <DataTable
          title="Documentos asociados"
          data={procedure?.detailDocuments
            ?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map(doc => ({
              ...doc,
              date: getFormatedUtcDate(doc.date)
            }))}
          columns={dataTableDocumentsColumns}
          options={dataTableDocumentsOptions}
          info={DataTableErrorHandler(errorProps)}
        />
      </DialogContent>
      <DialogActions style={{ padding: currentTheme.spacing(2) }}>
        <Button size="small" color="primary" onClick={handleClose}>
          {t('comunicationProcedures:forms.mainActions.back')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
